import { useZoomService } from '@src/hooks/useZoomService';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useState } from 'react';

import { getStoryNameFromFileName } from '../utils';

import type { ZoomRecordingData } from './useStoryCreator';
import type { ZoomRecording } from '@src/services/ZoomService';

const logger = LogManager.getLogger('hook:useZoomImport');

/**
 * Zoom import state for the Add Stories Upload step: whether the person's Zoom
 * is connected, the recordings view, the picked recording, and disconnect.
 *
 * Moved out of the old `StepOne` unchanged (PLAT-11941) so the step's markup
 * could be rebuilt on the dialog scaffold without touching the OAuth flow.
 */
export function useZoomImport({
  setStoryName,
  setZoomRecordingData,
  handleStoryUrlUpload,
}: {
  setStoryName?: (s: string) => void;
  setZoomRecordingData?: (data: ZoomRecordingData | null) => void;
  handleStoryUrlUpload?: (url: string) => void;
}) {
  const { userIdentityStore } = useStore();
  const datoPerson = useDatoPerson();
  const zoomService = useZoomService();

  const [showZoomRecordings, setShowZoomRecordings] = useState(false);
  const [isZoomAuthenticated, setIsZoomAuthenticated] = useState(false);
  const [selectedZoomRecording, setSelectedZoomRecording] =
    useState<ZoomRecording | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  // Track if user explicitly disconnected to prevent auto re-auth
  const [explicitlyDisconnected, setExplicitlyDisconnected] = useState(false);
  // Track the personId that actually has the zoom connection (from OAuth)
  const [zoomPersonId, setZoomPersonId] = useState<string | null>(null);

  // Reset explicitlyDisconnected when component mounts (modal opens)
  useEffect(() => {
    setExplicitlyDisconnected(false);
  }, []);

  // Check if Zoom is already authenticated
  useEffect(() => {
    // Check for OAuth callback params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlPersonId = urlParams.get('personId');

    // Skip auth check if user explicitly disconnected or already authenticated
    if (explicitlyDisconnected || isZoomAuthenticated) {
      return;
    }

    // Check Zoom authentication status via backend API
    const checkZoomAuth = async () => {
      const effectivePersonId =
        urlPersonId || zoomPersonId || datoPerson?.person?.id;
      const email =
        userIdentityStore.currentIdentity?.email || datoPerson?.person?.email;

      // If we have a URL personId, update zoomPersonId state to track it
      if (urlPersonId && urlPersonId !== zoomPersonId) {
        setZoomPersonId(urlPersonId);
      }

      if (effectivePersonId || email) {
        const result = await zoomService.checkAuth(effectivePersonId, email);
        if (result.ok) {
          setIsZoomAuthenticated(result.value.authenticated);
        }
      }
    };

    checkZoomAuth();
  }, [
    datoPerson?.person?.id,
    userIdentityStore.currentIdentity?.email,
    explicitlyDisconnected,
    isZoomAuthenticated,
  ]);

  const selectRecording = (recording: ZoomRecording) => {
    setSelectedZoomRecording(recording);
    // Set story name from recording topic (same as video file upload behavior)
    if (setStoryName && recording.topic) {
      setStoryName(getStoryNameFromFileName(recording.topic, 120));
    }
  };

  const importSelectedRecording = () => {
    if (!selectedZoomRecording) return;

    // Set Zoom metadata for the upload pipeline to use
    // The backend will resolve the Zoom OAuth token server-side
    setZoomRecordingData?.({
      recordingFiles: selectedZoomRecording.recording_files,
      personId: zoomPersonId || undefined,
      email: userIdentityStore.currentIdentity?.email,
      password: selectedZoomRecording.password,
    });

    setShowZoomRecordings(false);
    setSelectedZoomRecording(null);

    // Use handleStoryUrlUpload with a placeholder URL
    // The actual Zoom download will be resolved server-side
    handleStoryUrlUpload?.('zoom-import://pending');
  };

  const handleAuthSuccess = (authData?: {
    personId?: string;
    email?: string;
  }) => {
    // Store the personId that has the zoom connection
    if (authData?.personId) {
      setZoomPersonId(authData.personId);
    }
    setIsZoomAuthenticated(true);
    // Don't automatically show recordings, let user click the button
  };

  const openRecordings = () => setShowZoomRecordings(true);
  const closeRecordings = () => {
    setShowZoomRecordings(false);
    setSelectedZoomRecording(null);
  };

  const disconnect = async () => {
    // CRITICAL: Use zoomPersonId (the person with zoom connection) not datoPerson
    const personIdToDisconnect = zoomPersonId || datoPerson?.person?.id;

    const result = await zoomService.disconnect(
      personIdToDisconnect,
      userIdentityStore.currentIdentity?.email,
    );

    if (result.ok) {
      setIsZoomAuthenticated(false);
      setShowZoomRecordings(false);
      setSelectedZoomRecording(null);
      setExplicitlyDisconnected(true);
      setZoomPersonId(null); // Clear the zoom person

      // Clear any lingering URL params
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.has('personId')) {
        currentUrl.searchParams.delete('personId');
        currentUrl.searchParams.delete('zoom_auth');
        window.history.replaceState({}, '', currentUrl.toString());
      }
    } else {
      logger.error('Failed to disconnect Zoom:', result.error.message);
    }

    setShowDisconnectModal(false);
  };

  return {
    isZoomAuthenticated,
    showZoomRecordings,
    selectedZoomRecording,
    zoomPersonId,
    showDisconnectModal,
    setShowDisconnectModal,
    selectRecording,
    importSelectedRecording,
    handleAuthSuccess,
    openRecordings,
    closeRecordings,
    disconnect,
  };
}
